import { Alert, Box, Stack, Typography } from '@mui/material';
import { Fragment, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { shallowEqual } from 'react-redux';
import { useAppDispatch, useAppSelector } from 'hooks/redux.hooks';
import { selectTranslations } from 'state/reducers/translation.reducers';
import { WebhookMonitoringStatusBucket } from 'constants/webhook-monitoring.constants';
import { TableParagraph } from 'theme/elements/Paragraphs';
import { ServerPaginatedTable } from 'components/table';
import { columnType, Order } from 'components/table/types';
import { DEFAULT_PAGE_SIZE, DEFAULT_SORT_ORDER } from 'constants/pagination.constants';
import { timing } from 'constants/ui.constants';
import { clearDetails, selectWebhookMonitoringTableAndDetails } from 'state/reducers/webhook-monitoring.reducer';
import {
	getWebhookMonitoringAnalytics,
	getWebhookMonitoringHealthcheck,
	getWebhookMonitoringOperationDetails,
	getWebhookMonitoringOperations,
	replayWebhookMonitoringOperation,
} from 'services/webhook-monitoring.service';
import { useConfirmationDialog } from 'hooks/shared/useConfirmationDialog';
import { ConfirmationDialog } from 'components/ConfirmationDialog';
import { useServerPagination, SortState } from 'hooks/pagination/useServerPagination';
import { DetailsDialog } from './components/DetailsDialog';
import { resolveGeneralTranslationLabel } from 'utils/webhook-monitoring.utils';

const INITIAL_SORT_BY = 'updatedAt';
const INITIAL_SORT_ORDER: Order = DEFAULT_SORT_ORDER;

const filtersForRequest = (filters?: Record<string, any>) =>
	filters && Object.keys(filters).length > 0 ? filters : undefined;

/** Deterministic JSON key for filter objects so equivalent filters do not double-fetch. */
const stableWebhookFiltersKey = (filters: Record<string, any>): string => {
	const topKeys = Object.keys(filters).sort();
	const normalized: Record<string, unknown> = {};
	for (const key of topKeys) {
		const v = filters[key];
		if (v != null && typeof v === 'object' && !Array.isArray(v) && !(v instanceof Date)) {
			const innerKeys = Object.keys(v).sort();
			const inner: Record<string, unknown> = {};
			for (const ik of innerKeys) {
				inner[ik] = (v as Record<string, unknown>)[ik];
			}
			normalized[key] = inner;
		} else if (Array.isArray(v)) {
			normalized[key] = [...v].sort();
		} else {
			normalized[key] = v;
		}
	}
	return JSON.stringify(normalized);
};

const sortFieldMap: Record<string, string> = {
	companyName: 'company_name',
	provider: 'provider',
	operationType: 'operation_type',
	businessStatus: 'business_status',
	status: 'status',
	operationId: 'created_at',
	createdAt: 'created_at',
	updatedAt: 'updated_at',
	retries: 'created_at',
	replayCount: 'created_at',
};

type Props = {
	status: WebhookMonitoringStatusBucket;
	filters: any;
	title: string;
	includeWebhookStatusColumn?: boolean;
};

export const MonitoringTable = ({ status, filters, title, includeWebhookStatusColumn = true }: Props) => {
	const dispatch = useAppDispatch();
	const t = useAppSelector(selectTranslations);
	const { table: tableState, details: detailsState } = useAppSelector(
		selectWebhookMonitoringTableAndDetails,
		shallowEqual
	);
	const { dialogConfig, openDialog, closeDialog, handleConfirm, handleCancel } = useConfirmationDialog();
	const [selectedRow, setSelectedRow] = useState<any>(null);
	const filtersKey = stableWebhookFiltersKey(filters);
	const currentPageRef = useRef(0);
	const filtersRef = useRef(filters);

	const getApiSortField = useCallback((uiSortBy: string) => {
		return sortFieldMap[uiSortBy].toString();
	}, []);

	const fetchOperations = useCallback(
		async (page: number, sortOverride: SortState, currentFilters?: Record<string, any>) => {
			await dispatch(
				getWebhookMonitoringOperations({
					status,
					offset: page * DEFAULT_PAGE_SIZE,
					limit: DEFAULT_PAGE_SIZE,
					sortBy: getApiSortField(sortOverride.sortBy),
					sortOrder: sortOverride.sortOrder,
					filters: filtersForRequest(currentFilters),
				})
			);
		},
		[dispatch, status, getApiSortField]
	);

	const { currentPage, isLoading, runFetch, handlePageChange, handleSortChange } = useServerPagination<
		Record<string, any> | undefined
	>({
		initialSortBy: INITIAL_SORT_BY,
		initialSortOrder: INITIAL_SORT_ORDER,
		fetchPage: fetchOperations,
	});

	currentPageRef.current = currentPage;
	filtersRef.current = filters;

	const closeDetails = useCallback(() => {
		setSelectedRow(null);
		dispatch(clearDetails());
	}, [dispatch]);

	useEffect(() => {
		runFetch(0, undefined, filters);
	}, [filtersKey, status, runFetch]);

	useEffect(() => {
		if (!detailsState.shouldCloseAfterFailure) {
			return;
		}
		closeDetails();
	}, [detailsState.shouldCloseAfterFailure, closeDetails]);

	const handlePageChangeWithFilters = useCallback(
		(page: number) => handlePageChange(page, filters),
		[handlePageChange, filters]
	);
	const handleSortChangeWithFilters = useCallback(
		(nextSortBy: string, nextSortOrder: Order) => handleSortChange(nextSortBy, nextSortOrder, filters),
		[handleSortChange, filters]
	);

	const handleReplay = useCallback(
		(operation: any) => {
			const webhookStatus = t(`page.webhookMonitoring.status.${operation.status}`);
			const replayDetails = [
				{ label: t('page.webhookMonitoring.field.operationId'), value: operation.operationId },
				{ label: t('page.webhookMonitoring.field.webhookStatus'), value: webhookStatus },
			];

			openDialog({
				title: t('page.webhookMonitoring.actions.replay.confirm.title'),
				content: (
					<Stack spacing={2} style={{ direction: 'rtl' }} sx={{ WebkitFontSmoothing: 'antialiased' }}>
						<Typography variant="body1" sx={{ color: 'text.primary', textWrap: 'pretty' }}>
							{t('page.webhookMonitoring.actions.replay.confirm.message')}
						</Typography>
						<Box
							sx={{
								border: '1px solid',
								borderColor: 'divider',
								borderRadius: 1.5,
								overflow: 'hidden',
							}}
						>
							{replayDetails.map((detail, index) => (
								<Stack
									key={detail.label}
									direction="row"
									justifyContent="space-between"
									gap={2}
									sx={{
										px: 1.5,
										py: 1,
										bgcolor: index % 2 === 0 ? 'background.paper' : 'root.grayed',
									}}
								>
									<Typography variant="subtitle2" sx={{ color: 'text.primary', fontWeight: 600 }}>
										{detail.label}
									</Typography>
									<Typography
										variant="body2"
										sx={{
											color: 'text.primary',
											textAlign: 'left',
											wordBreak: 'break-all',
											fontVariantNumeric: 'tabular-nums',
										}}
									>
										{detail.value}
									</Typography>
								</Stack>
							))}
						</Box>
						<Alert severity="warning" style={{ direction: 'rtl' }} sx={{ alignItems: 'flex-start' }}>
							{t('page.webhookMonitoring.actions.replay.confirm.warning')}
						</Alert>
					</Stack>
				),
				confirmLabel: t('page.webhookMonitoring.actions.replay'),
				cancelLabel: t('actions.cancel'),
				confirmColor: 'primary',
				onConfirm: async () => {
					const action = await dispatch(replayWebhookMonitoringOperation(operation.operationId));
					if (replayWebhookMonitoringOperation.rejected.match(action)) {
						closeDialog();
						return;
					}
					closeDialog();
					if (selectedRow) {
						dispatch(getWebhookMonitoringOperationDetails(selectedRow));
					}
					dispatch(getWebhookMonitoringAnalytics());
					dispatch(getWebhookMonitoringHealthcheck());
					runFetch(currentPageRef.current, undefined, filtersRef.current);
				},
			});
		},
		[openDialog, closeDialog, t, dispatch, selectedRow, runFetch]
	);

	const handleRowClick = useCallback(
		(row: any) => {
			setSelectedRow(row);
			dispatch(getWebhookMonitoringOperationDetails(row));
		},
		[dispatch]
	);

	useEffect(() => {
		if (!selectedRow) {
			return;
		}

		const selectedRowStillExists = tableState.data.some((row: any) => row.operationId === selectedRow.operationId);
		if (!selectedRowStillExists) {
			closeDetails();
		}
	}, [tableState.data, selectedRow, closeDetails]);

	const columns = useMemo(() => {
		const baseColumns: columnType[] = [
			{
				fieldId: 'operationId',
				name: 'companyName',
				title: t('page.webhookMonitoring.field.companyName'),
				type: 'string',
				sortable: true,
			},
			{
				fieldId: 'operationId',
				name: 'provider',
				title: t('page.webhookMonitoring.field.provider'),
				type: 'custom',
				sortable: true,
				customValueHandle: (value: any) => t(`page.webhookMonitoring.provider.${value}`),
			},
			{
				fieldId: 'operationId',
				name: 'operationType',
				title: t('page.webhookMonitoring.field.operationType'),
				type: 'custom',
				sortable: true,
				customValueHandle: (value: any) => resolveGeneralTranslationLabel(value),
			},
			{
				fieldId: 'operationId',
				name: 'businessStatus',
				title: t('page.webhookMonitoring.field.businessStatus'),
				type: 'custom',
				sortable: true,
				customValueHandle: (value: any) => resolveGeneralTranslationLabel(value),
			},
			...(includeWebhookStatusColumn
				? [
						{
							fieldId: 'operationId',
							name: 'status',
							title: t('page.webhookMonitoring.field.webhookStatus'),
							type: 'custom',
							sortable: true,
							customValueHandle: (value: any) => t(`page.webhookMonitoring.status.${value}`),
						} as columnType,
				  ]
				: []),
			{
				fieldId: 'operationId',
				name: 'operationId',
				title: t('page.webhookMonitoring.field.operationId'),
				type: 'string',
				sortable: false,
			},
			{
				fieldId: 'operationId',
				name: 'createdAt',
				title: t('page.webhookMonitoring.field.createdAt'),
				type: 'date',
				sortable: true,
			},
			{
				fieldId: 'operationId',
				name: 'updatedAt',
				title: t('page.webhookMonitoring.field.updatedAt'),
				type: 'date',
				sortable: true,
			},
			{
				fieldId: 'operationId',
				name: 'retries',
				title: t('page.webhookMonitoring.field.retries'),
				type: 'integer',
				sortable: false,
			},
			{
				fieldId: 'operationId',
				name: 'replayCount',
				title: t('page.webhookMonitoring.field.replayCount'),
				type: 'integer',
				sortable: false,
			},
		];

		return baseColumns;
	}, [includeWebhookStatusColumn, t]);

	const serverPagination = useMemo(
		() => ({
			total: tableState.pagination.total,
			hasMore: tableState.pagination.hasMore,
			currentPage,
			onPageChange: handlePageChangeWithFilters,
			onSortChange: handleSortChangeWithFilters,
			isLoading,
		}),
		[
			tableState.pagination.total,
			tableState.pagination.hasMore,
			currentPage,
			handlePageChangeWithFilters,
			handleSortChangeWithFilters,
			isLoading,
		]
	);

	return (
		<Fragment>
			<Stack spacing={2}>
				<TableParagraph title={title} text={`${tableState.pagination.total} ${t('messages.results')}`} />
				<ServerPaginatedTable
					defaultSortBy={INITIAL_SORT_BY}
					defaultOrderBy={INITIAL_SORT_ORDER}
					data={tableState.data}
					columns={columns}
					perPage={DEFAULT_PAGE_SIZE}
					rowAction={handleRowClick}
					serverPagination={serverPagination}
					dateFormat={timing.FULLDATE_WITH_SECONDS}
				/>
			</Stack>
			<DetailsDialog
				open={selectedRow !== null}
				row={selectedRow}
				detailsState={detailsState}
				onClose={closeDetails}
				onReplay={handleReplay}
			/>
			<ConfirmationDialog
				open={dialogConfig.open}
				title={dialogConfig.title}
				message={dialogConfig.message}
				content={dialogConfig.content}
				confirmLabel={dialogConfig.confirmLabel}
				cancelLabel={dialogConfig.cancelLabel}
				onConfirm={handleConfirm}
				onCancel={handleCancel}
				loading={dialogConfig.loading}
				confirmColor={dialogConfig.confirmColor}
			/>
		</Fragment>
	);
};
